import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Grid, IconButton, Paper, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { platformKpiService } from '../../services/platform/platformKpi.service';
import { PERIODS } from '../../utils/platformKpi';
import InventoryRow from '../../components/platform-dashboard/InventoryRow';
import RevenuePanel from '../../components/platform-dashboard/RevenuePanel';
import UsageLadder from '../../components/platform-dashboard/UsageLadder';
import PriorityGrid from '../../components/platform-dashboard/PriorityGrid';
import AttentionList from '../../components/platform-dashboard/AttentionList';
import CentersTable from '../../components/platform-dashboard/CentersTable';
import CenterDrawer from '../../components/platform-dashboard/CenterDrawer';
import AdoptionHeatmap from '../../components/platform-dashboard/AdoptionHeatmap';
import ValueCostPanel from '../../components/platform-dashboard/ValueCostPanel';
import GrowthPanel from '../../components/platform-dashboard/GrowthPanel';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'growth', label: 'Growth' },
  { key: 'centers', label: 'Centers' },
  { key: 'value', label: 'Value & cost' },
];

const QUICK_LINKS = [
  { label: 'Typing centers', to: '/typing-centers' },
  { label: 'Subscriptions', to: '/admin/subscriptions' },
  { label: 'Activity', to: '/activity' },
  { label: 'AI usage', to: '/admin/ai-usage' },
];

/**
 * Platform Admin KPI dashboard (PLATFORM-KPI-DESIGN.md §4).
 * Rendered by Dashboard.js for PLATFORM_ADMIN / PLATFORM_SUPPORT only.
 */
const PlatformDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('mtd');
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);
  const [drawerCenter, setDrawerCenter] = useState(null);
  const [health, setHealth] = useState('checking');
  const reqId = useRef(0);

  const load = useCallback(async (p) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await platformKpiService.load(p);
      if (id === reqId.current) setData(result);
    } catch (err) {
      if (id === reqId.current) setError('Could not load typing centers.');
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  useEffect(() => {
    ApiService.get('/api/health')
      .then((res) => setHealth(res?.data?.data?.status === 'healthy' ? 'healthy' : 'issues'))
      .catch(() => setHealth('down'));
  }, []);

  const showInTable = (f) => {
    setFilter(f);
    setTab('centers');
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const sources = data?.sources || {};
  const anySample = Object.values(sources).includes('sample');
  const centers = data?.centers || [];

  return (
    <Box>
      {/* Header — period chips, last updated, refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
        {tab === 'growth' && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Last 12 months, month by month</Typography>
        )}
        {tab !== 'growth' && PERIODS.map((p) => (
          <Chip
            key={p.key}
            label={p.label}
            size="small"
            color={period === p.key ? 'primary' : 'default'}
            onClick={() => setPeriod(p.key)}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        ))}
        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 1 }}>
          {data?.generatedAt ? `Updated ${data.generatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={{ healthy: 'API responding', issues: 'API reporting issues', down: 'API not responding', checking: 'Checking API' }[health]}>
          <Chip
            size="small"
            label={`API ${{ healthy: 'healthy', issues: 'issues', down: 'down', checking: '…' }[health]}`}
            sx={{
              height: 22, fontSize: '0.65rem', color: '#fff',
              bgcolor: { healthy: '#73BF69', issues: '#D97706', down: '#F2495C', checking: 'grey.500' }[health],
            }}
          />
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => load(period)}
          disabled={loading}
          sx={{ bgcolor: 'primary.main', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' } }}
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} action={<Button size="small" onClick={() => load(period)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {anySample && !error && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0, fontSize: '0.8125rem' }}>
          Some figures are sample data until the platform KPI API is ready. Sections marked “Sample” are not real yet.
        </Alert>
      )}

      {!data && loading && (
        <Grid container spacing={1.5}>
          {[0, 1, 2].map((i) => <Grid item xs={12} key={i}><Skeleton variant="rounded" height={140} /></Grid>)}
        </Grid>
      )}

      {data && (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600 } }}
          >
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>

          <Grid container spacing={1.5} sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            {tab === 'overview' && (
              <>
                <Grid item xs={12}>
                  <InventoryRow centers={centers} sources={sources} storageComputedAt={data.storageComputedAt} />
                </Grid>
                <Grid item xs={12}>
                  <RevenuePanel revenue={data.revenue} source={sources.revenue} />
                </Grid>
                <Grid item xs={12}>
                  <UsageLadder centers={centers} source={sources.usage} onSelectStage={(v) => showInTable({ type: 'stage', value: v })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PriorityGrid sx={{ height: '100%' }} centers={centers} source={sources.potential} onSelectQuadrant={(v) => showInTable({ type: 'quadrant', value: v })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AttentionList sx={{ height: '100%' }} centers={centers} source={sources.usage} onOpen={setDrawerCenter} />
                </Grid>
              </>
            )}

            {tab === 'growth' && (
              <Grid item xs={12}>
                <GrowthPanel growth={data.growth} />
              </Grid>
            )}

            {tab === 'centers' && (
              <>
                <Grid item xs={12}>
                  <CentersTable
                    centers={centers}
                    source={sources.usage}
                    filter={filter}
                    onClearFilter={() => setFilter(null)}
                    onOpen={setDrawerCenter}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AdoptionHeatmap centers={centers} source={sources.usage} onOpen={setDrawerCenter} />
                </Grid>
              </>
            )}

            {tab === 'value' && (
              <Grid item xs={12}>
                <ValueCostPanel centers={centers} source={sources.value} storageSource={sources.storage} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Paper sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 1 }}>Go to</Typography>
                {QUICK_LINKS.map((l) => (
                  <Button key={l.to} size="small" onClick={() => navigate(l.to)} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
                    {l.label}
                  </Button>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <CenterDrawer center={drawerCenter} sources={sources} open={!!drawerCenter} onClose={() => setDrawerCenter(null)} />
    </Box>
  );
};

export default PlatformDashboard;
